import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError, handleToggleError } from "../utils/errorHandler";
import CommonPagination from "../Components/Pagination";
import {
  Box,
  Container,
  Paper,
  Tooltip,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CssBaseline,
  Menu,
  Switch,
  MenuItem,
  Popover,
  FormControl,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

function Substage_details() {
  const [formData, setFormData] = useState({
    cl_id: "",
    client_name: "",
    env_id: "",
    app_id: "",
    job_id: "",
    substage_id: "",
    substage_desc: "",
    comments: "",
  });
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const optionalFields = ["substage_id", "comments"];

  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    environment_name: true,
    application_name: true,
    jobs_script: true,
    substage_id: true,
    substage_desc: true,
    comments: true,
  });
  const [filters, setFilters] = useState({
    client_name: "",
    environment_name: "",
    application_name: "",
    jobs_script: "",
    substage_id: "",
    substage_desc: "",
    comments: "",
  });
  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    application_name: "Application",
    jobs_script: "Job",
    substage_id: "Substage ID",
    substage_desc: "Substage",
    comments: "Comments",
  };
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const validate = () => {
    const errors = {};
    
    // Required fields validation
    if (!formData.env_id) errors.env_id = "Environment is required";
    if (!formData.app_id) errors.app_id = "Application is required";
    if (!formData.job_id) errors.job_id = "Job ID is required";
    
    // Substage Description validation
    if (!formData.substage_desc || !formData.substage_desc.trim()) {
      errors.substage_desc = "Substage Description is required";
    } else {
      const value = formData.substage_desc.trim();
      
      // Check if it contains only special characters
      if (/^[^a-zA-Z0-9]+$/.test(value)) {
        errors.substage_desc = "Substage Description cannot contain only special characters";
      }
      // Check if it contains only numbers
      else if (/^\d+$/.test(value)) {
        errors.substage_desc = "Substage Description cannot contain only numbers";
      }
      // Check if it contains at least one letter
      else if (!/[a-zA-Z]/.test(value)) {
        errors.substage_desc = "Substage Description must contain at least one letter";
      }
    }
    
    // Validate comments field if provided
    if (formData.comments && formData.comments.trim()) {
      const value = formData.comments.trim();
      
      // Check if it contains only special characters
      if (/^[^a-zA-Z0-9]+$/.test(value)) {
        errors.comments = "Comments cannot contain only special characters";
      }
      // Check if it contains only numbers
      else if (/^\d+$/.test(value)) {
        errors.comments = "Comments cannot contain only numbers";
      }
    }
    
    return errors;
  };
   const compactMultilineStyles = {
    "& .MuiInputBase-root": {
      minHeight: "80px",
      fontSize: "0.85rem",
      padding: "0 10px",
    },
    "& .MuiInputBase-input": {
      padding: "8px 10px",
      lineHeight: "1.2",
      overflowY: "auto",
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.85rem",
      top: "-2px",
    },
    "& .MuiInputLabel-shrink": {
      top: "0",
    },
    "& .MuiFormHelperText-root": {
      fontSize: "0.875rem",
    },
  };


  const fetchClients = async () => {
    try {
      const data = await apiService.get("/substage/clients");
      setClients(data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/substage/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error fetching environments:", err);
    }
  };

  const fetchApplications = async (envId = null) => {
    try {
      let url = "/substage/applications";
      if (envId) {
        url += `?env_id=${envId}`;
      }
      const data = await apiService.get(url);
      setApplications(data);
    } catch (err) {
      console.error("Error fetching applications:", err);
      setApplications([]);
    }
  };

  const fetchJobs = async (envId = null, appId = null) => {
    try {
      let url = "/substage/jobs";
      const params = new URLSearchParams();
      if (envId) params.append("env_id", envId);
      if (appId) params.append("app_id", appId);
      if (params.toString()) url += `?${params.toString()}`;

      const data = await apiService.get(url);
      setJobs(data);
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setJobs([]);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim() !== "") {
        params.append(key, value);
      }
    });

    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm);
    }

    try {
      const result = await apiService.get(
        `/substage/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, searchTerm]);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients]);

  useEffect(() => {
    fetchClients();
    fetchApplications(); // Load all applications initially
    fetchEnvironments();
    fetchJobs(); // Load all jobs initially
    fetchData();
  }, [fetchData]);

  // Track form changes for edit mode
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEnvironmentChange = (event, newValue) => {
    const envId = newValue ? newValue.env_id : "";
    setFormData((prev) => ({
      ...prev,
      env_id: envId,
      app_id: "", // Reset application when environment changes
      job_id: "", // Reset job when environment changes
    }));
    setFormErrors((prev) => ({ ...prev, env_id: "" }));

    // Fetch applications for the selected environment
    if (envId) {
      fetchApplications(envId);
    } else {
      setApplications([]);
    }

    // Clear dependent dropdowns
    setJobs([]);
  };

  const handleApplicationChange = (event, newValue) => {
    const appId = newValue ? newValue.app_id : "";
    setFormData((prev) => ({
      ...prev,
      app_id: appId,
      job_id: "", // Reset job when application changes
    }));
    setFormErrors((prev) => ({ ...prev, app_id: "" }));

    // Fetch jobs for the selected environment and application
    if (formData.env_id && appId) {
      fetchJobs(formData.env_id, appId);
    } else {
      setJobs([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      if (isEditing) {
        await apiService.put(`/substage/${formData.substage_id}`, formData);
      } else {
        await apiService.post("/substage", formData);
      }

      setSuccessMessage(
        isEditing
          ? "Substage updated successfully!"
          : "Substage added successfully!",
      );

      // Reset form to initial state
      setFormData({
        cl_id: clients.length > 0 ? clients[0].cl_id : "",
        client_name: clients.length > 0 ? clients[0].name : "",
        env_id: "",
        app_id: "",
        job_id: "",
        substage_id: "",
        substage_desc: "",
        comments: "",
      });
      setFormErrors({});
      setIsEditing(false);
      setApplications([]);
      setJobs([]);
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
      setOpenDialog(false);
    } catch (error) {
      console.error("Error saving substage:", error);
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(error, "Substage")
        : handleCreateError(error, "Substage");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleEdit = (row) => {
    const editFormData = {
      cl_id: row.cl_id || "",
      client_name: row.client_name || "",
      env_id: row.env_id || "",
      app_id: row.app_id || "",
      job_id: row.job_id || "",
      substage_id: row.substage_id || "",
      substage_desc: row.substage_desc || "",
      comments: row.comments || "",
    };
    
    setFormData(editFormData);
    setOriginalFormData({ ...editFormData }); // Store original data for comparison
    setIsEditing(true);
    setIsFormChanged(false); // Reset change flag
    setFormErrors({});

    // Load dependent data for editing in the correct order
    if (row.env_id) {
      fetchApplications(row.env_id);
      if (row.app_id) {
        fetchJobs(row.env_id, row.app_id);
      }
    }

    setOpenDialog(true);
  };

  const handleDeleteClick = (row) => {
    setDeleteRow(row);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;

    try {
      await apiService.delete(
        `/substage/${deleteRow.substage_id}?cl_id=${deleteRow.cl_id}&env_id=${deleteRow.env_id}&app_id=${deleteRow.app_id}&job_id=${deleteRow.job_id}`
      );

      setSuccessMessage("Substage deleted successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchData();
      setOpenDeleteDialog(false);
      setDeleteRow(null);
    } catch (error) {
      setOpenDeleteDialog(false);
      setDeleteRow(null);
      
      // Use centralized error handler
      const errorMessage = handleDeleteError(error, "Substage");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const cancelDelete = () => {
    setOpenDeleteDialog(false);
    setDeleteRow(null);
  };

  const handleDialogOpen = async () => {
    setFormErrors({});
    setIsEditing(false);

    let nextId = "";
    try {
      const { substage_id } = await apiService.get("/substage/next-id");
      nextId = substage_id.toString();
    } catch (e) {
      console.error("Could not fetch next substage id", e);
    }

    const fresh = {
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      app_id: "",
      job_id: "",
      substage_id: nextId,
      substage_desc: "",
      comments: "",
    };

    setFormData(fresh);

    // Clear dependent dropdowns when opening new form
    setApplications([]);
    setJobs([]);

    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    // Reset form data and clear dependent dropdowns
    setFormData({
      cl_id: clients.length > 0 ? clients[0].cl_id : "",
      client_name: clients.length > 0 ? clients[0].name : "",
      env_id: "",
      app_id: "",
      job_id: "",
      substage_id: "",
      substage_desc: "",
      comments: "",
    });
    setFormErrors({});
    setIsEditing(false);
    setOriginalFormData({});
    setIsFormChanged(false);
    setApplications([]);
    setJobs([]);
    setOpenDialog(false);
  };

  const columns = [
    "client_name",
    "environment_name",
    "application_name",
    "jobs_script",
    "substage_id",
    "substage_desc",
    "comments",
  ];

  const formFields = [
    {
      label: "Client",
      name: "cl_id",
      type: "readonly",
      required: true,
    },
    {
      label: "Environment",
      name: "env_id",
      type: "select",
      options: environments,
      optionKey: "env_id",
      optionLabel: "name",
      onChange: handleEnvironmentChange,
      required: true,
    },
    {
      label: "Application",
      name: "app_id",
      type: "select",
      options: applications,
      optionKey: "app_id",
      optionLabel: "name",
      onChange: handleApplicationChange,
      disabled: !formData.env_id,
      required: true,
    },
    {
      label: "Job",
      name: "job_id",
      type: "select",
      options: jobs,
      optionKey: "job_id",
      optionLabel: "jobs_script",
      formatOption: (job) => `${job.jobs_script}`,
      disabled: !formData.app_id,
      required: true,
    },
    {
      label: "Substage Description",
      name: "substage_desc",
      type: "text",
      multiline: true,
  rows: 2,
      required: true,
    },
    {
      label: "Comments",
      name: "comments",
      type: "text",
      multiline: true,
  rows: 2,
      required: true,
    },
  ];

  const handleToggleFlag = async (substage) => {
    try {
      const newFlag = parseInt(substage.active_flag) === 1 ? 0 : 1;

      if (!substage.substage_id) {
        throw new Error("Invalid substage_id");
      }

      await apiService.put(`/substage/${substage.substage_id}`, {
        cl_id: substage.cl_id,
        env_id: substage.env_id,
        app_id: substage.app_id,
        job_id: substage.job_id,
        active_flag: newFlag,
        isActiveFlagOnly: true,
      });

      fetchData();
      
      // Show success message
      const message = newFlag === 1 
        ? "Substage Activated" 
        : "Substage Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      // Use centralized error handler
      const errorMessage = handleToggleError(error, "Substage");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Substages Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Substages
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={handleDialogOpen}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>

              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2DB0E4",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>
      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col]}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 2}
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((row) => (
                      <TableRow key={row.substage_id}>
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleToggleFlag(row)}
                              title="Toggle Active Flag"
                              disabled={!row.substage_id}
                              sx={{
                                opacity: row.substage_id ? 1 : 0.5,
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    row.active_flag != null &&
                                    parseInt(row.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(row)}
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {columns.map(
                          (col) =>
                            visibleColumns[col] && (
                              <TableCell key={col}>{row[col]}</TableCell>
                            ),
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={columns.length + 2}
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>

        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Dialog
            open={openDialog}
            // onClose={handleDialogClose}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              setOpenDialog(false);
            }}
            width="small"
            maxWidth="md"
          >
            <DialogTitle
              sx={{
                fontWeight: "700",
                fontSize: "16px",
                py: 1,
              }}
            >
              {isEditing ? "Edit" : "Add"} Substage
              <IconButton
                onClick={handleDialogClose}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2.1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                {formFields.map((field) =>
                  field.type === "readonly" ? (
                    <TextField
                      key={field.name}
                      label={field.label}
                      value={
                        formData.client_name ||
                        clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                        ""
                      }
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                         minWidth: 300,
                        backgroundColor: "white",

                        "& .MuiInputBase-input": {
                          cursor: "not-allowed",
                          color: "grey",
                        },
                      }}
                      fullWidth
                      margin="normal"
                    />
                  ) : field.type === "select" ? (
                    <FormControl
                      key={field.name}
                      fullWidth
                      margin="none"
                      error={!!formErrors[field.name]}
                    >
                      <Autocomplete
                        id={field.name}
                        options={field.options}
                        getOptionLabel={(option) =>
                          field.formatOption
                            ? field.formatOption(option)
                            : option[field.optionLabel] || ""
                        }
                        value={
                          field.options.find(
                            (option) =>
                              option[field.optionKey] === formData[field.name],
                          ) || null
                        }
                        onChange={
                          field.onChange ||
                          ((event, newValue) => {
                            setFormData((prev) => ({
                              ...prev,
                              [field.name]: newValue
                                ? newValue[field.optionKey]
                                : "",
                            }));
                            setFormErrors((prev) => ({
                              ...prev,
                              [field.name]: "",
                            }));
                          })
                        }
                        ListboxProps={{
                          sx: {
                            "& .MuiAutocomplete-option": {
                              fontSize: "0.875rem",
                              minHeight: "32px",
                            },
                          },
                        }}
                        renderInput={(params) => (
                          // <TextField
                          //   {...params}
                          //   label={field.label}
                          //   required={field.required}
                          //   error={!!formErrors[field.name]}
                          //   //required={field.name === "env_id" || field.name === "app_id" || field.name === "job_id"}
                          //   helperText={
                          //     formErrors[field.name] || field.helperText
                          //   }
                          // />
                          <TextField
  key={field.name}
  label={field.label}
  name={field.name}
  type={field.type || "text"}
  value={formData[field.name] || ""}
  onChange={handleChange}
  fullWidth
  required={!optionalFields.includes(field.name)}
  margin="normal"
  error={!!formErrors[field.name]}
  helperText={formErrors[field.name]}
  multiline={field.multiline || false}
  rows={field.multiline ? field.rows || 3 : undefined}
  sx={{
    ...(field.multiline ? compactMultilineStyles : {}),
    width: "100%",
    ...(field.multiline && { gridColumn: "span 2" }),
  }}
  InputProps={{
    readOnly: field.readOnly,
    style: field.readOnly
      ? { backgroundColor: "#f9f9f9" }
      : {},
  }}
/>
                        )}
                        disabled={field.disabled || field.options.length === 0}
                        noOptionsText={
                          field.helperText || "No options available"
                        }
                      />
                    </FormControl>
//                   ) : (
//                     <TextField
//   key={field.name}
//   label={
//     !optionalFields.includes(field.name)
//       ? `${field.label}`
//       : field.label
//   }
//   name={field.name}
//   type={field.type || "text"}
//   value={formData[field.name] || ""}
//   onChange={handleChange}
//   fullWidth
//   required={!optionalFields.includes(field.name)}
//   margin="normal"
//   error={!!formErrors[field.name]}
//   helperText={formErrors[field.name]}
//   InputProps={{
//     readOnly: field.readOnly,
//     style: field.readOnly
//       ? { backgroundColor: "#f9f9f9" }
//       : {},
//   }}
// />
) : (
  <TextField
    key={field.name}
    label={
      !optionalFields.includes(field.name)
        ? `${field.label}`
        : field.label
    }
    name={field.name}
    type={field.type || "text"}
    value={formData[field.name] || ""}
    onChange={handleChange}
    fullWidth
    required={!optionalFields.includes(field.name)}
    margin="normal"
    error={!!formErrors[field.name]}
    helperText={formErrors[field.name]}
    multiline={field.multiline || false}
    rows={field.multiline ? field.rows || 2 : undefined}
    sx={{
      ...(field.multiline ? compactMultilineStyles : {}),
      ...(field.multiline && { gridColumn: "span 2" }),
    }}
    InputProps={{
      readOnly: field.readOnly,
      style: field.readOnly
        ? { backgroundColor: "#f9f9f9" }
        : {},
    }}
  />
)

                  ,
                )}
                {formErrors.submit && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {formErrors.submit}
                  </Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleDialogClose}>Cancel</Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained"
                disabled={isEditing && !isFormChanged}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={(event, reason) => {
              if (reason === "backdropClick") return;
              cancelDelete();
            }}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Substage ID{" "}
                <strong>{deleteRow?.substage_id}</strong> ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button onClick={cancelDelete} sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}>
                Cancel
              </Button>

              <Button 
                onClick={confirmDelete} 
                variant="contained"
                sx={{ fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif" }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
}

export default Substage_details;
